import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CertificationsService,
  LanguageService,
  SpecialExperiencesService,
  SpecialServicesService,
  SpecialSituationsService,
  TeachingSkillsService,
} from 'us-web-services';

import { ToggleSwitch } from '../ToggleSwitch';

// Shared filter sidebar for Sitters search and JobInterest lists.
// Angular source: apps/angular/src/js/ng/parent/components/sitters-search-filter-body/

export interface SittersSearchFilterValue {
  distance?: number;
  certificationIds?: number[];
  languageIds?: number[];
  teachingSkillIds?: number[];
  specialSituationIds?: number[];
  specialExperienceIds?: number[];
  specialServiceIds?: number[];
  minRate?: number;
  maxRate?: number;
  acceptsCreditCard?: boolean;
  name?: string;
}

interface Option {
  id?: number;
  name?: string;
  label?: string;
}

interface Props {
  value: SittersSearchFilterValue;
  onChange: (next: SittersSearchFilterValue) => void;
  isJobInterest?: boolean;
}

const DISTANCES = [1, 3, 5, 10, 15, 25, 50];

interface FacetProps {
  title: string;
  options: Option[];
  selected: number[];
  onToggle: (id: number) => void;
}

const FacetMultiSelect = ({ title, options, selected, onToggle }: FacetProps): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const count = selected.length;
  return (
    <div className='border-b border-neutral-200 py-2'>
      <button
        type='button'
        className='w-full flex justify-between items-center font-semibold'
        onClick={(): void => setOpen(!open)}
      >
        <span>
          {title}
          {count > 0 && (
            <span className='ml-1 text-sm text-sapphire'>({count})</span>
          )}
        </span>
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} />
      </button>
      {open && (
        <div className='mt-2 space-y-1 max-h-64 overflow-auto'>
          {options.map((opt) => {
            const id = opt.id as number;
            return (
              <label key={id} className='flex items-center gap-2 text-sm'>
                <input
                  type='checkbox'
                  checked={selected.includes(id)}
                  onChange={(): void => onToggle(id)}
                />
                <span>{opt.name ?? opt.label}</span>
              </label>
            );
          })}
          {options.length === 0 && (
            <p className='text-sm text-neutral-500'>{t('components.sittersFilter.loading')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export const SittersSearchFilter = ({
  value,
  onChange,
  isJobInterest,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [certifications, setCertifications] = useState<Option[]>([]);
  const [languages, setLanguages] = useState<Option[]>([]);
  const [teachingSkills, setTeachingSkills] = useState<Option[]>([]);
  const [specialSituations, setSpecialSituations] = useState<Option[]>([]);
  const [specialExperiences, setSpecialExperiences] = useState<Option[]>([]);
  const [specialServices, setSpecialServices] = useState<Option[]>([]);

  useEffect(() => {
    const load = async <T extends Option>(
      svc: { getByFilter: () => Promise<{ data?: { data?: T[] } }> },
      setter: (data: T[]) => void,
    ): Promise<void> => {
      try {
        const r = await svc.getByFilter();
        setter((r.data?.data ?? []) as T[]);
      } catch {
        setter([] as T[]);
      }
    };
    void load(CertificationsService, setCertifications);
    void load(LanguageService, setLanguages);
    void load(TeachingSkillsService, setTeachingSkills);
    void load(SpecialSituationsService, setSpecialSituations);
    void load(SpecialExperiencesService, setSpecialExperiences);
    void load(SpecialServicesService, setSpecialServices);
  }, []);

  const toggleId = (
    current: number[] | undefined,
    id: number,
  ): number[] => {
    const list = current ?? [];
    return list.includes(id) ? list.filter((n) => n !== id) : [...list, id];
  };

  const update = (patch: Partial<SittersSearchFilterValue>): void => {
    onChange({ ...value, ...patch });
  };

  const clearAll = (): void => {
    onChange({});
  };

  const distance = value.distance ?? 10;

  return (
    <div className='us-card rounded p-3'>
      <div className='flex justify-between items-center mb-2'>
        <h4 className='font-semibold'>{t('components.sittersFilter.header')}</h4>
        <button type='button' className='text-sm underline' onClick={clearAll}>
          {t('components.sittersFilter.clear')}
        </button>
      </div>

      {!isJobInterest && (
        <div className='border-b border-neutral-200 py-2'>
          <label htmlFor='filter-distance' className='block font-semibold mb-1'>
            {t('components.sittersFilter.distance')}
          </label>
          <select
            id='filter-distance'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={distance}
            onChange={(e): void => update({ distance: Number(e.target.value) })}
          >
            {DISTANCES.map((d) => (
              <option key={d} value={d}>
                {t('components.sittersFilter.distanceOption', { count: d })}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className='border-b border-neutral-200 py-2'>
        <label htmlFor='filter-max-rate' className='block font-semibold mb-1'>
          {t('components.sittersFilter.rateHeader')}
          <span className='font-normal'>
            {value.maxRate
              ? t('components.sittersFilter.rateAndUnder', { maxRate: value.maxRate })
              : t('components.sittersFilter.rateAny')}
          </span>
        </label>
        <div className='flex items-center gap-2'>
          <span className='text-sm'>{t('components.sittersFilter.rateUnder')}</span>
          <div className='flex items-center border border-neutral-300 rounded px-2 focus-within:border-sapphire focus-within:ring-1 focus-within:ring-sapphire'>
            <span className='text-neutral-600'>$</span>
            <input
              id='filter-max-rate'
              type='tel'
              inputMode='numeric'
              pattern='[0-9]*'
              aria-label={t('components.sittersFilter.rateMaxAriaLabel')}
              maxLength={3}
              className='w-12 px-1 py-2 outline-none focus:outline-none focus:ring-0 border-0'
              value={value.maxRate ?? ''}
              onChange={(e): void =>
                update({
                  maxRate: e.target.value
                    ? Number(e.target.value.replace(/\D/g, ''))
                    : undefined,
                })
              }
            />
          </div>
          <span className='text-sm'>{t('components.sittersFilter.ratePerHour')}</span>
        </div>
      </div>

      <div className='border-b border-neutral-200 py-2'>
        <div className='flex items-center justify-between gap-2 font-semibold'>
          <label htmlFor='filter-accepts-credit-card' className='cursor-pointer'>
            {t('components.sittersFilter.acceptsCreditCard')}
          </label>
          <ToggleSwitch
            id='filter-accepts-credit-card'
            checked={!!value.acceptsCreditCard}
            handleClick={(): void =>
              update({ acceptsCreditCard: value.acceptsCreditCard ? undefined : true })
            }
          />
        </div>
      </div>

      <FacetMultiSelect
        title={t('components.sittersFilter.certifications')}
        options={certifications}
        selected={value.certificationIds ?? []}
        onToggle={(id): void =>
          update({ certificationIds: toggleId(value.certificationIds, id) })
        }
      />
      <FacetMultiSelect
        title={t('components.sittersFilter.languages')}
        options={languages}
        selected={value.languageIds ?? []}
        onToggle={(id): void =>
          update({ languageIds: toggleId(value.languageIds, id) })
        }
      />
      <FacetMultiSelect
        title={t('components.sittersFilter.teachingSkills')}
        options={teachingSkills}
        selected={value.teachingSkillIds ?? []}
        onToggle={(id): void =>
          update({ teachingSkillIds: toggleId(value.teachingSkillIds, id) })
        }
      />
      <FacetMultiSelect
        title={t('components.sittersFilter.specialSituations')}
        options={specialSituations}
        selected={value.specialSituationIds ?? []}
        onToggle={(id): void =>
          update({ specialSituationIds: toggleId(value.specialSituationIds, id) })
        }
      />
      <FacetMultiSelect
        title={t('components.sittersFilter.specialExperiences')}
        options={specialExperiences}
        selected={value.specialExperienceIds ?? []}
        onToggle={(id): void =>
          update({ specialExperienceIds: toggleId(value.specialExperienceIds, id) })
        }
      />
      <FacetMultiSelect
        title={t('components.sittersFilter.specialServices')}
        options={specialServices}
        selected={value.specialServiceIds ?? []}
        onToggle={(id): void =>
          update({ specialServiceIds: toggleId(value.specialServiceIds, id) })
        }
      />

      <NameSearch
        value={value.name}
        onChange={(next): void => update({ name: next })}
      />
    </div>
  );
};

interface NameSearchProps {
  value?: string;
  onChange: (next: string | undefined) => void;
}

const NameSearch = ({ value, onChange }: NameSearchProps): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const apply = (): void => {
    const trimmed = draft.trim();
    onChange(trimmed ? trimmed : undefined);
    setOpen(false);
  };

  const clear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setDraft('');
    onChange(undefined);
  };

  return (
    <div className='py-2'>
      <button
        type='button'
        id='search-by-name-link'
        className='w-full flex justify-between items-center font-semibold text-left'
        onClick={(): void => setOpen((o) => !o)}
      >
        <span>
          {t('components.sittersFilter.name')}
          {value && <span className='ml-1 font-normal'>{value}</span>}
        </span>
        {value ? (
          <button
            type='button'
            id='clear-name'
            aria-label={t('components.sittersFilter.clearName')}
            onClick={clear}
            className='text-neutral-500 hover:text-neutral-800 px-2'
          >
            <i className='fa-solid fa-xmark' />
          </button>
        ) : (
          <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} />
        )}
      </button>
      {open && (
        <div className='mt-2 space-y-2'>
          <p className='text-sm text-neutral-700 mb-1'>{t('components.sittersFilter.searchByName')}</p>
          <input
            aria-label={t('components.sittersFilter.caregiverNameAriaLabel')}
            type='text'
            id='search-name-text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={draft}
            onChange={(e): void => setDraft(e.target.value)}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') apply();
            }}
          />
          <button
            type='button'
            id='search-name'
            onClick={apply}
            className='w-full bg-sapphire text-white rounded px-3 py-2 text-sm font-semibold'
          >
            {t('components.sittersFilter.search')}
          </button>
        </div>
      )}
    </div>
  );
};
